import React from "react";
import {
  Container,
  Typography,
  Box,
  Divider,
  Link,
  Paper,
  Stack,
} from "@mui/material";

const LAST_UPDATED = "May 7, 2026";
const CONTACT_EMAIL = "privacy@mysharestock.org";
const COMPANY_NAME = "ShareStock";

const READING_WIDTH = 720;

const listSx = {
  pl: 3,
  mt: 1,
  mb: 0,
  "& li": { mb: 1, lineHeight: 1.75 },
  "& li:last-of-type": { mb: 0 },
};

const bodySx = { lineHeight: 1.75 };

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box component="section" sx={{ mb: 5 }}>
    <Typography
      variant="h5"
      component="h2"
      sx={{ fontWeight: 600, mb: 2 }}
    >
      {title}
    </Typography>
    <Stack spacing={2}>{children}</Stack>
  </Box>
);

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body1" sx={bodySx}>
    {children}
  </Typography>
);

export const PrivacyPage = () => {
  return (
    <Container maxWidth="md" sx={{ mt: 6, mb: 8, textAlign: "left" }}>
      <Box sx={{ maxWidth: READING_WIDTH, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            textAlign: "left",
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{ fontWeight: 700, mb: 1 }}
            >
              Privacy Policy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last updated: {LAST_UPDATED}
            </Typography>
          </Box>

          <Divider sx={{ mb: 5 }} />

          <Section title="1. Overview">
            <Body>
              {COMPANY_NAME} ("we," "us," "our") operates a consumer
              marketplace as a Shopify sales channel and at
              mysharestock.org. This Privacy Policy explains what
              information we collect, how we use it, and the choices you
              have. It applies to Merchants (Shopify store operators),
              Buyers (shoppers who purchase on the marketplace), and
              visitors to our website.
            </Body>
          </Section>

          <Section title="2. Information We Collect">
            <Body>
              <strong>From Merchants (via Shopify).</strong> When you
              install our app, Shopify shares your shop domain, store name,
              and an OAuth access token that lets us call the Shopify Admin
              API on your behalf within the scopes you approved. We use
              those scopes to read product, inventory, and order data
              needed to publish listings to the marketplace and surface
              order activity in our embedded admin.
            </Body>
            <Body>
              <strong>From Buyers (via marketplace registration).</strong>{" "}
              When you create a marketplace account, we collect your name,
              email, and authentication credentials (passwords are stored
              only as bcrypt hashes — we never see plaintext).
            </Body>
            <Body>
              <strong>Transaction data.</strong> Orders placed through the
              marketplace are completed by the Merchant's Shopify Checkout.
              We may store cart references and order identifiers needed to
              display your purchase history; we do not store payment card
              details. Payment processing is handled entirely by Shopify
              and the Merchant's payment provider.
            </Body>
            <Body>
              <strong>Technical data.</strong> Standard server logs (IP
              address, user agent, request paths) are recorded for security
              and debugging.
            </Body>
          </Section>

          <Section title="3. How We Use Information">
            <Box component="ul" sx={listSx}>
              <Typography component="li" variant="body1" sx={bodySx}>
                To operate the marketplace and the Shopify sales channel.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                To authenticate Merchants via Shopify session tokens and
                Buyers via marketplace credentials.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                To display listings, surface orders, and process cart
                sessions.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                To send transactional email (account verification, password
                resets, order-related notifications).
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                To prevent fraud, abuse, and security incidents.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                To comply with legal obligations.
              </Typography>
            </Box>
            <Body>
              We do not sell personal information. We do not share Merchant
              or Buyer data with advertisers.
            </Body>
          </Section>

          <Section title="4. Sharing With Service Providers">
            <Body>
              We share data with the third parties strictly necessary to
              run the Service:
            </Body>
            <Box component="ul" sx={listSx}>
              <Typography component="li" variant="body1" sx={bodySx}>
                <strong>Shopify</strong> — the underlying platform; we send
                and receive data through the Admin API and Storefront API
                under the scopes you granted at install.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                <strong>Microsoft Azure</strong> — application hosting,
                database (Azure SQL), and email (Azure Communication
                Services).
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                <strong>Legal authorities</strong> — only when required by
                law, court order, or to protect rights and safety.
              </Typography>
            </Box>
          </Section>

          <Section title="5. Cookies and Local Storage">
            <Body>
              On the marketplace web app at mysharestock.org we use
              first-party cookies and localStorage to keep you signed in and
              to remember UI preferences. We do not use third-party
              advertising cookies. The embedded sales-channel admin inside
              Shopify Admin authenticates via short-lived Shopify session
              tokens (60-second JWTs) and does not depend on third-party
              cookies.
            </Body>
          </Section>

          <Section title="6. Data Retention">
            <Body>
              We retain Merchant Shopify connection records, listings, and
              marketplace user accounts for as long as your account is
              active. When a Merchant uninstalls the app, our
              app/uninstalled webhook deletes the connection record.
              Shopify also fires the shop/redact webhook 48 hours after
              uninstall — at that point we delete any remaining
              shop-scoped data, including cart references and cached
              storefront tokens. Server log retention is limited to 90 days
              unless required for an active investigation.
            </Body>
          </Section>

          <Section title="7. Your Rights">
            <Body>
              Depending on where you live, you may have the right to
              access, correct, export, or delete personal information we
              hold about you. You may exercise these rights by emailing{" "}
              <Link href={`mailto:${CONTACT_EMAIL}`} underline="hover">
                {CONTACT_EMAIL}
              </Link>
              .
            </Body>
            <Body>
              For data Shopify customers (your store's buyers) request
              through Shopify's privacy webhooks (customers/data_request
              and customers/redact), we respond within Shopify's required
              windows. Shopify customers should direct privacy requests to
              the Merchant they purchased from; the Merchant forwards them
              to us through Shopify's webhook system.
            </Body>
          </Section>

          <Section title="8. Security">
            <Body>
              We protect data in transit with TLS, store passwords only as
              bcrypt hashes, and verify Shopify webhook signatures with
              constant-time HMAC comparison. Access to production data is
              limited to engineering staff who need it. No system is
              perfectly secure; if you believe your account has been
              compromised, contact us immediately at{" "}
              <Link href={`mailto:${CONTACT_EMAIL}`} underline="hover">
                {CONTACT_EMAIL}
              </Link>
              .
            </Body>
          </Section>

          <Section title="9. International Transfers">
            <Body>
              Our infrastructure runs on Microsoft Azure in the United
              States. If you are accessing the Service from outside the
              United States, your data will be transferred to and processed
              in the United States. By using the Service, you consent to
              this transfer.
            </Body>
          </Section>

          <Section title="10. Children">
            <Body>
              The Service is intended for businesses and is not directed to
              children under 16. We do not knowingly collect personal
              information from children.
            </Body>
          </Section>

          <Section title="11. Changes to This Policy">
            <Body>
              We may update this Privacy Policy from time to time. If we
              make material changes, we will notify users through the
              Service or by email at least 14 days before the changes take
              effect. The "Last updated" date at the top of this page
              reflects the most recent revision.
            </Body>
          </Section>

          <Section title="12. Contact">
            <Body>
              Privacy questions or requests:{" "}
              <Link href={`mailto:${CONTACT_EMAIL}`} underline="hover">
                {CONTACT_EMAIL}
              </Link>
            </Body>
          </Section>
        </Paper>
      </Box>
    </Container>
  );
};

export default PrivacyPage;
